import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ExternalLink, Clipboard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import useWordpressSettings from "@/hooks/useWordpressSettings";

export default function WordPressAuthenticationDialog() {
  const { currentWebsite } = useWebsites();
  const { settings: wpSettings } = useWordPress();
  const {
    isAuthenticating,
    setIsAuthenticating,
    showAuthDialog,
    setShowAuthDialog,
    wpUsername,
    setWpUsername,
    wpPassword,
    setWpPassword,
    connectionError,
    setConnectionError,
    connectionDiagnostics,
    setConnectionDiagnostics,
    setDirectWpSettings,
  } = useSettingsState();
  const { testEdgeFunction } = useWordpressSettings();

  const parseWordPressData = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      
      // Parse the copied text format:
      // WordPress URL: https://openocean.dk
      // Username: philiplethcom
      // Application Password: ZlidUdf3TwFJvdUOmJtBXnKh
      
      const lines = clipboardText.split('\n');
      let wpUrl = '';
      let username = '';
      let password = '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('WordPress URL:')) {
          wpUrl = trimmedLine.replace('WordPress URL:', '').trim();
        } else if (trimmedLine.startsWith('Username:')) {
          username = trimmedLine.replace('Username:', '').trim();
        } else if (trimmedLine.startsWith('Application Password:')) {
          password = trimmedLine.replace('Application Password:', '').trim();
        }
      }
      
      if (username && password) {
        setWpUsername(username);
        setWpPassword(password);
        
        // If we have a URL and it's different from current website, show a note
        if (wpUrl && currentWebsite?.url && wpUrl !== currentWebsite.url) {
          toast.info(`Note: Copied URL (${wpUrl}) differs from current website (${currentWebsite.url})`);
        }
        
        toast.success('WordPress data inserted successfully!');
      } else {
        toast.error('Could not parse WordPress data. Please make sure you copied the data from the WordPress plugin.');
      }
    } catch (error) {
      console.error('Error reading clipboard:', error);
      toast.error('Could not read clipboard. Please paste the data manually.');
    }
  };

  const handleCompleteWordPressAuth = async () => {
    // Field validations
    if (!wpUsername.trim()) {
      setConnectionError("WordPress username is required");
      return;
    }

    if (!wpPassword.trim()) {
      setConnectionError("WordPress password is required");
      return;
    }

    if (!currentWebsite) {
      toast.error("No website selected. Please select a website first");
      console.log("Error: No website selected for WordPress authentication");
      return;
    }

    // Clear previous error states when starting authentication
    setConnectionError(null);
    setConnectionDiagnostics(null);
    setIsAuthenticating(true);
    try {
      // Clean and validate the URL
      let url = currentWebsite.url.trim();

      // Make sure the URL is properly formatted
      if (!url.match(/^https?:\/\//)) {
        url = "https://" + url;
        console.log("URL adjusted to include protocol:", url);
      }

      // Remove any trailing slashes
      url = url.replace(/\/+$/, "");

      console.log("Attempting direct WordPress auth for website:", url);

      // Try direct connection with basic auth credentials
      // Use a URL that's known to work (WordPress.org) to store our credentials directly
      console.log("Trying direct WordPress authentication...");

      // Create credentials for saving to database
      const wpSettings = {
        website_id: currentWebsite.id,
        wp_url: url,
        wp_username: wpUsername,
        wp_application_password: wpPassword,
        is_connected: true,
        updated_at: new Date().toISOString(),
      };

      // Save to database without testing (trust user input)
      console.log("Saving WordPress settings directly to database");
      try {
        // Check if there's already a record for this website
        const { data: existingSettings, error: fetchError } = await supabase
          .from("wordpress_settings")
          .select("*")
          .eq("website_id", currentWebsite.id)
          .limit(1);

        if (fetchError && fetchError.code !== "PGRST116") {
          // Real error (not "no rows returned" error)
          console.error("Error checking for existing settings:", fetchError);
          toast.error("Failed to check for existing WordPress settings");
          setConnectionError("Database error: " + fetchError.message);
          return;
        }

        let result;
        if (existingSettings && existingSettings.length > 0) {
          // Update existing record
          console.log("Updating existing WordPress settings");
          result = await supabase
            .from("wordpress_settings")
            .update(wpSettings)
            .eq("website_id", currentWebsite.id)
            .select()
            .limit(1);
        } else {
          // Insert new record
          console.log("Inserting new WordPress settings");
          result = await supabase
            .from("wordpress_settings")
            .insert(wpSettings)
            .select()
            .limit(1);
        }

        const { data, error } = result;

        if (error) {
          console.error("Database error:", error);
          toast.error("Failed to save WordPress settings");
          setConnectionError("Database error: " + error.message);
          return;
        }

        // Update the directWpSettings state with the new data
        if (data && data.length > 0) {
          setDirectWpSettings(data[0]);
        }

        setShowAuthDialog(false);
        setWpUsername("");
        setWpPassword("");
        toast.success("WordPress connection saved successfully");
        console.log("WordPress settings saved to database");

        // Offer to test the connection right after saving
        const testConnection = window.confirm(
          "WordPress connection saved. Would you like to test the connection now?"
        );
        if (testConnection) {
          // Test the connection
          try {
            console.log(`Testing WordPress connection to: ${url}`);

            // Show notice about development environment
            if (window.location.hostname === "localhost") {
              toast.info(
                "Note: Connection tests may return detailed errors to help with troubleshooting",
                {
                  duration: 6000, // Show for 6 seconds
                }
              );
            }

            // Use our direct test function with the Edge Function
            if (
              wpSettings &&
              wpSettings.wp_username &&
              wpSettings.wp_application_password
            ) {
              await testEdgeFunction(
                wpSettings.wp_url,
                wpSettings.wp_username,
                wpSettings.wp_application_password
              );
            } else {
              toast.error("WordPress credentials not found");
            }
          } catch (testError) {
            console.error("Connection test error:", testError);
            toast.error(
              `Connection test failed: ${
                testError instanceof Error ? testError.message : String(testError)
              }`
            );

            // Still show a positive message
            toast.info(
              `Your WordPress connection details are saved and may work for publishing even if testing fails.`
            );
          }
        }
      } catch (dbError) {
        console.error("Database operation error:", dbError);
        toast.error("Error saving WordPress settings to the database");
        setConnectionError(
          "Database error: " +
            (dbError instanceof Error ? dbError.message : String(dbError))
        );
      }
    } catch (error) {
      console.error("Error during WordPress authentication:", error);
      toast.error(
        "Failed to connect to WordPress. Please check your credentials and URL."
      );
      setConnectionError(
        "Authentication error: " +
          (error instanceof Error ? error.message : String(error))
      );
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
      <DialogContent
        className="sm:max-w-[650px] max-h-[85vh] flex flex-col"
        onEscapeKeyDown={(e) => {
          if (isAuthenticating) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {wpSettings ? "Edit WordPress Connection" : "Connect to WordPress"}
          </DialogTitle>
          <DialogDescription>
            {wpSettings
              ? "Update your WordPress credentials"
              : "Enter your WordPress credentials to connect your site"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              console.log("Form submitted via submit event");
              if (!wpUsername || !wpPassword) {
                toast.error("Please enter both username and application password");
                console.log("Validation failed: missing username or password");
                return;
              }
              handleCompleteWordPressAuth();
            }}
          >
            <div className="space-y-4 py-4">
              <div className="rounded-lg border bg-muted/50 p-4 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">Creating your Connection Key:</h4>
                <ol className="list-decimal pl-4 space-y-2 text-sm text-muted-foreground">
                  <li>
                    In your WordPress admin, go to <b>Users → Profile</b>
                  </li>
                  <li>
                    Scroll down to <b>Application Passwords</b> section
                  </li>
                  <li>Enter "ContentGardener.ai" as the name</li>
                  <li>
                    Click <b>Add New Application Password</b>
                  </li>
                  <li>
                    Copy the generated password <b>exactly as shown</b> - it should look
                    like: xxxx xxxx xxxx xxxx
                  </li>
                  <li>Paste it below along with your WordPress username</li>
                </ol>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      if (currentWebsite?.url) {
                        const wpUrl = currentWebsite.url.replace(/\/$/, "");
                        window.open(
                          `${wpUrl}/wp-admin/profile.php#application-passwords-section`,
                          "_blank"
                        );
                      } else {
                        toast.error("Please enter your WordPress website URL first");
                      }
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open WordPress Profile
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm">
                <p className="font-medium text-yellow-800">Using Security Plugins?</p>
                <p className="text-yellow-800 mt-1">
                  If you're using security plugins like Wordfence, Sucuri, or iThemes
                  Security, you may need to enable Application Passwords in their
                  settings.
                </p>
                <p className="text-yellow-800 mt-1">
                  Look for settings like "Application Passwords", "REST API", or "XML-RPC"
                  and make sure they're enabled.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wpUsername">WordPress Username</Label>
                <Input
                  id="wpUsername"
                  name="wpUsername"
                  value={wpUsername}
                  onChange={(e) => {
                    console.log("Username input changed:", e.target.value);
                    setWpUsername(e.target.value);
                  }}
                  placeholder="Enter your WordPress username"
                  autoComplete="username"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  This is the username you use to log in to WordPress
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wpPassword">Connection Key</Label>
                <Input
                  id="wpPassword"
                  name="wpPassword"
                  type="text"
                  className="font-mono"
                  value={wpPassword}
                  onChange={(e) => {
                    // Preserve the exact format including spaces which are critical
                    console.log(
                      "Password input changed (length):",
                      e.target.value.length
                    );
                    setWpPassword(e.target.value);
                  }}
                  placeholder="xxxx xxxx xxxx xxxx"
                  autoComplete="off"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Paste the connection key <b>exactly as shown</b>, including all spaces.
                  It should look like <code>xxxx xxxx xxxx xxxx</code>.
                </p>
                {wpPassword && !wpPassword.includes(" ") && (
                  <div className="text-xs text-red-600 mt-2 p-2 border border-red-300 bg-red-50 rounded">
                    <p className="font-medium">
                      Important: Missing spaces in your connection key
                    </p>
                    <p className="mt-1">
                      WordPress application passwords normally include spaces between each
                      4-character group
                    </p>
                    <p className="mt-1">
                      Copy the entire password directly from WordPress and paste it
                      without modifications.
                    </p>
                  </div>
                )}
                {wpPassword && wpPassword.length > 40 && (
                  <div className="text-xs text-amber-600 mt-2 p-2 border border-amber-300 bg-amber-50 rounded">
                    <p className="font-medium">Warning: Unusually long password</p>
                    <p className="mt-1">
                      Your password is {wpPassword.length} characters long, which is
                      unusual for a WordPress application password.
                    </p>
                    <p className="mt-1">
                      Most WordPress application passwords are between 24-32 characters
                      with spaces.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={parseWordPressData}
                  className="flex items-center gap-2"
                >
                  <Clipboard className="h-4 w-4" />
                  Insert All Data from Clipboard
                </Button>
                <p className="text-xs text-muted-foreground ml-3">
                  Paste the copied data from WordPress plugin and click this button
                </p>
              </div>

              {isAuthenticating && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm mb-4">
                  <p className="font-medium flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting to WordPress...
                  </p>
                  <p className="text-muted-foreground mt-1">
                    This is happening in our secure cloud function and may take a few
                    moments
                  </p>
                </div>
              )}

              {currentWebsite?.url && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
                  <p className="font-medium">
                    Connecting to:{" "}
                    <span className="text-blue-600">{currentWebsite.url}</span>
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Make sure this URL matches your WordPress site and is accessible
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    <strong>Expected format:</strong> example.com or www.example.com
                    (we'll add https:// automatically)
                  </p>
                </div>
              )}

              {connectionError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                  <p className="font-medium text-red-700">Connection Error:</p>
                  <p className="text-red-700 mt-1">{connectionError}</p>

                  {connectionDiagnostics && (
                    <div className="mt-3">
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium text-gray-600">
                          Technical Details (for troubleshooting)
                        </summary>
                        <div className="mt-2 p-2 bg-white/50 rounded-sm border border-gray-200 font-mono">
                          <p>
                            <strong>URL Tested:</strong> {connectionDiagnostics.urlTested}
                          </p>
                          <p>
                            <strong>Status:</strong> {connectionDiagnostics.statusCode}{" "}
                            {connectionDiagnostics.statusText}
                          </p>
                        </div>
                      </details>
                    </div>
                  )}

                  <div className="mt-3 text-xs text-gray-700">
                    <p className="font-medium">Common solutions:</p>
                    <ul className="list-disc pl-4 mt-1 space-y-1">
                      <li>Make sure your WordPress site is publicly accessible</li>
                      <li>Ensure the REST API is enabled in WordPress</li>
                      <li>
                        Check that your application password was copied correctly
                        (including spaces)
                      </li>
                      <li>
                        Try using your WordPress admin username instead of your email
                        address
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            </div>
          </form>
        </div>
        
        <DialogFooter className="flex-shrink-0 pt-4 border-t">
          <Button
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              if (!isAuthenticating) {
                setShowAuthDialog(false);
                console.log("Connection modal closed by user");
              }
            }}
            disabled={isAuthenticating}
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log("Complete Connection button clicked");
              handleCompleteWordPressAuth();
            }}
            disabled={!wpUsername || !wpPassword || isAuthenticating}
            type="submit"
          >
            {isAuthenticating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Connection"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
